import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { internationalEmail } from "@/utils/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { CustomToastComponent } from "@/components/shared";
import { createUserWithEmail, signInWithEmail, signInWithGoogle, signInWithMicrosoft } from "@/firebase";
import { useLoginUserLazyQueryHook, useValidateSignupSessionLazyQueryHook } from "@/hooks/queries";
import { UserCredential } from "firebase/auth";
import { FcGoogle } from "react-icons/fc";
import { FaMicrosoft } from "react-icons/fa";
import PasswordInput from "@/components/ui/password-input";
import { RotatingLinesLoader } from "@/components/shared/loaders";

/**
 * Creates the schema for the AcceptInvite form validation
 * Uses international email validation to support German umlauts (ä, ö, ü, ß)
 *
 * @param t - Translation function for error messages
 * @returns Zod schema object for form validation
 */
const createAcceptInviteSchema = (t: (_key: string) => string) => {
  return z.object({
    email: internationalEmail(t("acceptinvite.validation.emailinvalid")),
    password: z
      .string()
      .min(8, t("acceptinvite.validation.passwordtooshort")),
    confirmPassword: z
      .string()
      .min(1, t("acceptinvite.validation.confirmpasswordrequired"))
  }).refine((data) => data.password === data.confirmPassword, {
    message: t("acceptinvite.validation.passwordmismatch"),
    path: ["confirmPassword"],
  });
};

type AcceptInviteFormData = z.infer<ReturnType<typeof createAcceptInviteSchema>>;

const AcceptInvite = () => {

  // Hooks
  const { t } = useTranslation(["translation", "toasts"]);
  const { sessionID } = useParams<{ sessionID: string }>();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get("email");
  const navigate = useNavigate();

  // States
  const [email, setEmail] = useState<string | null>(emailParam || sessionStorage.getItem("email"));
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(true);

  // Form setup with Zod validation
  const acceptInviteSchema = createAcceptInviteSchema(t);
  const form = useForm<AcceptInviteFormData>({
    resolver: zodResolver(acceptInviteSchema),
    mode: "onChange",
    defaultValues: {
      email: email || "",
      password: "",
      confirmPassword: "",
    },
  });

  // Queries
  const { loginUser } = useLoginUserLazyQueryHook();
  const { validateSignupSession } = useValidateSignupSessionLazyQueryHook(setEmail, form, setIsValidating);

  useEffect(() => {
    // Early exit if no sessionID is present
    if (!sessionID) {
      navigate("/login", { replace: true });
      return;
    }

    try {
      validateSignupSession({ variables: { sessionId: sessionID } });
    } catch (err) {
      console.log("Error has occurred", err);
      navigate("/login", { replace: true });
    }
  }, [sessionID, navigate, validateSignupSession]);

  const handleAuthError = () => {
    toast(CustomToastComponent, {
      toastId: "accept-invite-auth-error",
      data: {
        title: t("toasts:toasts.somethingwentwrong"),
        type: "error",
      }
    });
  };

  /**
   * Handles OAuth login during invite acceptance.
   * Ensures the provider email matches the invited email and logs in via backend.
   */
  const handleOAuthLogin = async (fn: () => Promise<UserCredential>): Promise<void> => {
    try {
      setIsSubmitting(true);
      const result = await fn();
      const providerEmail = result.user.email ?? undefined;
      if (email && providerEmail && email.toLowerCase() !== providerEmail.toLowerCase()) {
        toast(CustomToastComponent, {
          toastId: "accept-invite-email-mismatch",
          data: { title: t("toasts:toasts.somethingwentwrong") || t("toasts:toasts.incorrectemailsso"), type: "error" }
        });
        setIsSubmitting(false);
        return;
      }
      const token = await result.user.getIdToken(true);
      loginUser({ variables: { oAuthToken: token } });
    } catch (_error) {
      handleAuthError();
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (data: AcceptInviteFormData) => {
    if (!data.email) return;

    setIsSubmitting(true);
    try {
      let token: string | null = null;
      try {
        const userCredential = await createUserWithEmail(data.email, data.password);
        token = await userCredential.user.getIdToken(true);
      } catch (err: any) {
        // If user already exists, try to sign in with provided password
        if (err?.code === "auth/email-already-in-use") {
          const userCredential = await signInWithEmail(data.email, data.password);
          token = await userCredential.user.getIdToken(true);
        } else {
          throw err;
        }
      }

      if (token) {
        // Login against Backend which will verify token and set cookie
        loginUser({ variables: { sessionToken: sessionID } });
      } else {
        handleAuthError();
      }
    } catch (_error) {
      handleAuthError();
      setIsSubmitting(false);
    }
  };

  if (isValidating || !email) {
    return (
      <div className="flex items-center justify-center w-full h-screen">
        <div className="flex flex-col items-center">
          <p className="text-2xl mb-2">
            {t("tokenlogin.verifying")}
          </p>
          <RotatingLinesLoader
            width={74}
            animationDuration={0.75}
            strokeColor="#42A7DC"
            strokeWidth={8}
            ariaLabel="rotating-lines-loader"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center w-full h-screen">
      <div className="w-[420px] p-6 shadow-sm">
        <div className="space-y-1.5 mb-4">
          <h2 className="text-2xl font-semibold tracking-tight">{t("acceptinvite.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("acceptinvite.description")}</p>
        </div>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="email">{t("acceptinvite.email")}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t("acceptinvite.email")}
              {...form.register("email")}
              disabled
            />
            {form.formState.errors.email && (
              <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="password">{t("acceptinvite.password")}</Label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              {...form.register("password")}
            />
            {form.formState.errors.password && (
              <p className="text-sm text-destructive">{form.formState.errors.password.message}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="confirmPassword">{t("acceptinvite.confirmpassword")}</Label>
            <PasswordInput
              id="confirmPassword"
              hideStrengthUI
              autoComplete="new-password"
              placeholder={t("acceptinvite.confirmpassword")}
              {...form.register("confirmPassword")}
            />
            {form.formState.errors.confirmPassword && (
              <p className="text-sm text-destructive">{form.formState.errors.confirmPassword.message}</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting || !form.formState.isValid}>
            {isSubmitting ? t("acceptinvite.creatingaccount") : t("acceptinvite.createaccountandcontinue")}
          </Button>
          <div className="relative my-2 py-2">
            <Separator />
            <p className="text-muted-foreground absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-sm shrink-0 px-2 bg-white">
              {t("acceptinvite.orcontinuewith")}
            </p>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <Button
              onClick={() => { handleOAuthLogin(signInWithGoogle); }}
              variant="outline"
              className="flex-1 text-gray-800 flex items-center justify-center transition-all duration-300"
              disabled={isSubmitting}
            >
              <FcGoogle size={20} className="mr-2" />
              Google
            </Button>

            <Button
              onClick={() => { handleOAuthLogin(signInWithMicrosoft); }}
              className="flex-1 bg-[#2F2F2F] hover:bg-[#1F1F1F] text-white flex items-center justify-center transition-all duration-300"
              disabled={isSubmitting}
            >
              <FaMicrosoft size={20} className="mr-2" />
              Microsoft
            </Button>
          </div>
        </form>
        <Separator className="mt-4 mb-2" />
        <div className="flex items-center justify-between text-sm gap-2">
          <p className=" text-gray-800 font-light">
            {t("acceptinvite.alreadyhaveaccount")}
          </p>
          <a onClick={() => { navigate("/login"); }} className="text-blue-400 cursor-pointer">
            {t("acceptinvite.signin")}
          </a>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
